const BASE_URL = 'https://influencers.chord-agency.com/backend/api';

function getToken() { return localStorage.getItem('token'); }
function authHeaders() {
  return { 'Content-Type': 'application/json', 'Authorization': `Bearer ${getToken()}` };
}

// Central response handler — redirects to login on 401 (session expired)
async function handleResponse(res) {
  const data = await res.json();
  if (res.status === 401) {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    window.location.href = '/login';
    throw new Error('Session expired — please log in again');
  }
  return { res, data };
}

export async function login(email, password) {
  const res  = await fetch(`${BASE_URL}/login.php`, {
    method: 'POST', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, password }),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || 'Login failed');
  localStorage.setItem('token', data.token);
  localStorage.setItem('user', JSON.stringify(data.user));
  return data;
}

export function logout() { localStorage.removeItem('token'); localStorage.removeItem('user'); }
export function getUser() { const u = localStorage.getItem('user'); return u ? JSON.parse(u) : null; }

export async function fetchStats(platform = null) {
  const url = platform ? `${BASE_URL}/stats.php?platform=${platform}` : `${BASE_URL}/stats.php`;
  const { res, data } = await handleResponse(await fetch(url, { headers: authHeaders() }));
  if (!res.ok) throw new Error(data.error || 'Failed');
  return data;
}

export async function fetchInfluencers(platform, filters = {}) {
  const params = new URLSearchParams({ platform });
  Object.entries(filters).forEach(([k, v]) => {
    if (v !== '' && v !== null && v !== undefined && v !== 0) params.append(k, v);
  });
  const { res, data } = await handleResponse(await fetch(`${BASE_URL}/influencers.php?${params}`, { headers: authHeaders() }));
  if (!res.ok) throw new Error(data.error || 'Failed');
  return data;
}

export async function addInfluencer(platform, payload) {
  const { res, data } = await handleResponse(await fetch(`${BASE_URL}/influencers.php?platform=${platform}`, {
    method: 'POST', headers: authHeaders(), body: JSON.stringify(payload),
  }));
  if (!res.ok) throw new Error(data.error || 'Failed to add');
  return data;
}

export async function updateInfluencer(platform, id, payload) {
  const { res, data } = await handleResponse(await fetch(`${BASE_URL}/influencer.php?platform=${platform}&id=${id}`, {
    method: 'PUT', headers: authHeaders(), body: JSON.stringify(payload),
  }));
  if (!res.ok) throw new Error(data.error || 'Failed to update');
  return data;
}

export async function deleteInfluencer(platform, id) {
  const { res, data } = await handleResponse(await fetch(`${BASE_URL}/influencer.php?platform=${platform}&id=${id}`, {
    method: 'DELETE', headers: authHeaders(),
  }));
  if (!res.ok) throw new Error(data.error || 'Failed to delete');
  return data;
}

export async function generateConnectLink(platform, influencer_id) {
  const { res, data } = await handleResponse(await fetch(`${BASE_URL}/ig_connect.php?platform=${platform}&influencer_id=${influencer_id}`, {
    headers: authHeaders(),
  }));
  if (!res.ok) throw new Error(data.error || 'Failed to generate link');
  return data;
}

export async function fetchIGData(platform, influencer_id) {
  const { res, data } = await handleResponse(await fetch(`${BASE_URL}/ig_fetch.php?platform=${platform}&influencer_id=${influencer_id}`, {
    headers: authHeaders(),
  }));
  if (!res.ok) throw new Error(data.error || 'Failed to fetch IG data');
  return data;
}

export async function bulkImport(platform, rows, mode = 'add') {
  const { res, data } = await handleResponse(await fetch(`${BASE_URL}/bulk_import.php?platform=${platform}`, {
    method: 'POST', headers: authHeaders(), body: JSON.stringify({ rows, mode }),
  }));
  if (!res.ok) throw new Error(data.error || 'Bulk import failed');
  return data;
}

export async function findDuplicates(platform) {
  const { res, data } = await handleResponse(await fetch(`${BASE_URL}/merge_duplicates.php?platform=${platform}&action=find`, { headers: authHeaders() }));
  if (!res.ok) throw new Error(data.error || 'Failed');
  return data;
}

export async function mergeDuplicates(platform, keepId, mergeIds) {
  const { res, data } = await handleResponse(await fetch(`${BASE_URL}/merge_duplicates.php?action=merge`, {
    method: 'POST', headers: authHeaders(),
    body: JSON.stringify({ platform, keep_id: keepId, merge_ids: mergeIds }),
  }));
  if (!res.ok) throw new Error(data.error || 'Failed to merge');
  return data;
}

export async function getCampaigns() {
  const { res, data } = await handleResponse(await fetch(`${BASE_URL}/campaigns.php`, { headers: authHeaders() }));
  if (!res.ok) throw new Error(data.error || 'Failed');
  return data;
}

export async function getCampaign(id) {
  const { res, data } = await handleResponse(await fetch(`${BASE_URL}/campaigns.php?id=${id}`, { headers: authHeaders() }));
  if (!res.ok) throw new Error(data.error || 'Failed');
  return data;
}

export async function createCampaign(name, subject, body) {
  const { res, data } = await handleResponse(await fetch(`${BASE_URL}/campaigns.php`, {
    method: 'POST', headers: authHeaders(), body: JSON.stringify({ name, subject, body }),
  }));
  if (!res.ok) throw new Error(data.error || 'Failed to create campaign');
  return data;
}

export async function deleteCampaign(id) {
  const { res, data } = await handleResponse(await fetch(`${BASE_URL}/campaigns.php?id=${id}`, {
    method: 'DELETE', headers: authHeaders(),
  }));
  if (!res.ok) throw new Error(data.error || 'Failed to delete');
  return data;
}

export async function sendCampaign(campaignId, recipients, subject, body) {
  const { res, data } = await handleResponse(await fetch(`${BASE_URL}/send_campaign.php`, {
    method: 'POST', headers: authHeaders(),
    body: JSON.stringify({ campaign_id: campaignId, recipients, subject, body }),
  }));
  if (!res.ok) throw new Error(data.error || 'Failed to send campaign');
  return data;
}

export async function sendOutreach(platform, recipients, subject, body, replyTo = '') {
  const { res, data } = await handleResponse(await fetch(`${BASE_URL}/send_outreach.php`, {
    method: 'POST', headers: authHeaders(), body: JSON.stringify({ platform, recipients, subject, body, reply_to: replyTo }),
  }));
  if (!res.ok) throw new Error(data.error || 'Failed to send emails');
  return data;
}

export async function sendTestReport(days = 7) {
  const { res, data } = await handleResponse(await fetch(`${BASE_URL}/send_test_report.php`, {
    method: 'POST', headers: authHeaders(), body: JSON.stringify({ days }),
  }));
  if (!res.ok) throw new Error(data.error || 'Failed to send test report');
  return data;
}

export async function generateFormLink(platform, label, expiresDays) {
  const { res, data } = await handleResponse(await fetch(`${BASE_URL}/form_token.php`, {
    method: 'POST', headers: authHeaders(), body: JSON.stringify({ platform, label, expires_days: expiresDays }),
  }));
  if (!res.ok) throw new Error(data.error || 'Failed to generate link');
  return data;
}

export async function verifyFormToken(platform, token) {
  const res  = await fetch(`${BASE_URL}/public_submit.php?platform=${platform}&token=${token}`);
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || 'Invalid link');
  return data;
}

export async function publicSubmitInfluencer(platform, token, payload) {
  const res  = await fetch(`${BASE_URL}/public_submit.php`, {
    method: 'POST', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ ...payload, platform, token }),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || 'Submission failed');
  return data;
}
